<template>
  <div>
    <div
      v-if="showUploadUI"
      class="attachment-drag-area pa-6"
      :class="{ 'drag-pointer-events-none': showOverlay }"
      @dragenter="handleDrag"
      @dragover="handleDrag"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <v-overlay
        :value="showOverlay"
        absolute
        :opacity="1.0"
        color="grey lighten-3"
      >
        <div class="text-center pb-2">
          <v-icon
            size="34"
            color="primary"
          >
            mdi-download
          </v-icon>
        </div>
        <div class="text-body-1 font-weight-medium primary--text text-center">
          Drop Files Here
        </div>
      </v-overlay>
      <div
        class="text-center"
        :class="{ 'pa-4': $isMobile }"
      >
        <input
          ref="fileInput"
          type="file"
          class="d-none"
          :multiple="multiple"
          :accept="inputAcceptedTypes"
          @change="handleFileInput"
        >
        <div
          class="text-body-1 font-weight-medium pb-2"
        >
          {{ dropPrompt }}
        </div>
        <div
          v-if="!$isMobile"
          class="text-body-2 pb-2"
        >
          or
        </div>
        <v-btn
          color="primary"
          @click="browseFilesClick"
        >
          {{ browseButtonText }}
        </v-btn>
      </div>
    </div>
    <div v-if="hasFiles">
      <div class="text-body-1 pt-4">
        {{ listTitle }}
      </div>
      <v-chip
        v-for="entry in files"
        :key="entry.id"
        class="attachment-file text-caption d-flex pa-3 mt-2"
        color="grey lighten-5"
        close
        close-icon="mdi-close"
        @click:close="handleRemove(entry.id)"
      >
        <v-icon
          contain
          size="20px"
          class="rotate-45 flex-grow-0 align-self-center pr-1"
        >
          mdi-paperclip
        </v-icon>
        <span class="flex-grow-1">
          {{ entry.file.name }}
        </span>
      </v-chip>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AttachmentUpload',

  props: {
    /**
     * Array containing objects of form:
     * { id: Number, file: File },
     */
    value: {
      type: Array,
      default: () => [],
    },

    dropPrompt: {
      type: String,
      default: 'Drag and drop your files here',
    },

    listTitle: {
      type: String,
      default: 'Files',
    },

    /**
     * Whether to allow multiple file uploads
     * When false (default), only one file can be uploaded at a time
     * and the upload UI is hidden when a file exists
     */
    multiple: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      files: this.value,
      isDragging: false,
      idCounter: 0,
      acceptedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/bmp'],
    };
  },

  watch: {
    // Watch for external changes to the value prop (e.g., when parent pre-populates files)
    value: {
      handler(newVal) {
        if (newVal && newVal !== this.files) {
          this.files = newVal;
          // Update idCounter to avoid duplicate ids when adding more files
          const maxId = newVal.reduce((max, f) => Math.max(max, f.id || 0), 0);
          if (maxId >= this.idCounter) {
            this.idCounter = maxId + 1;
          }
        }
      },
      deep: true,
    },
  },

  computed: {
    hasFiles() {
      return this.files.length > 0;
    },
    inputAcceptedTypes() {
      return this.acceptedTypes.join(',');
    },
    showOverlay() {
      return !this.$isMobile && this.isDragging;
    },
    // Hide upload UI in single file mode when a file exists
    showUploadUI() {
      if (!this.multiple && this.hasFiles) {
        return false;
      }
      return true;
    },
    browseButtonText() {
      if (!this.multiple) {
        return 'BROWSE FILE';
      }
      return this.hasFiles && this.files.length < 5 ? 'ADD MORE' : 'BROWSE FILE';
    },
  },

  methods: {
    /**
     * Accepts a FileList, checks mime type and adds numerical id
     * In single file mode (multiple=false), replaces existing file
     * In multiple file mode (multiple=true), appends files
     */
    addFiles(files) {
      // In single file mode, clear existing files before adding
      if (!this.multiple && this.files.length > 0) {
        this.files = [];
      }

      [...files].forEach((file) => { // FileList does not have .forEach
        if (file.type && this.acceptedTypes.includes(file.type)) {
          // In single file mode, only add if we don't already have a file
          if (!this.multiple && this.files.length > 0) {
            return;
          }
          this.files.push({ id: this.getUniqueId(), file });
        }
      });
      this.$emit('input', this.files);
    },

    getUniqueId() {
      this.idCounter += 1;
      return this.idCounter;
    },

    browseFilesClick() {
      // Open the hidden file input
      this.$refs.fileInput.click();
    },

    /**
     * Public method to trigger file input from parent component via ref
     */
    triggerFileInput() {
      this.browseFilesClick();
    },

    handleFileInput(e) {
      // Handles the browse files button
      this.addFiles(e.target.files);
    },

    handleDrag(e) {
      // Prevents default behaviour of drag
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = true;
    },

    handleDragLeave(e) {
      // Prevents default behaviour of drag leave
      e.stopPropagation();
      e.preventDefault();
      this.isDragging = false;
    },

    handleDrop(e) {
      // Handles drag and drop
      e.stopPropagation();
      e.preventDefault();
      this.addFiles(e.dataTransfer.files);
      this.isDragging = false;
    },

    handleRemove(id) {
      const index = this.files.findIndex((x) => x.id === id);
      if (index !== -1) {
        this.files.splice(index, 1);
        this.$emit('input', this.files);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/colors/colors';

.attachment-drag-area {
  background-color: #fafafa;
  border: 1px dashed #9E9E9E;
  border-radius: 8px;
  position: relative; /* To allow the overlay to position: absolute */
}

/* Needed to stop dragleave from firing when hovering a child */
.drag-pointer-events-none > * {
  pointer-events: none;
}

::v-deep .attachment-file .v-chip__content {
  width: 100%;
}
</style>
